import React, { useEffect, useContext, useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackward, faForward, faPlay, faPause, faClosedCaptioning } from '@fortawesome/free-solid-svg-icons';
import ProgressBar from './ProgressBar';
import { Container } from 'react-bootstrap';
import {getTrackMetadataValue} from './utility/MetadataFetcher';
import { WalkDataContext } from './hooks/walkDataContext';
import TranscriptOverlay from './common/overlays/TranscriptOverlay';

const PlayerDyn = React.memo(({
  audioRef,
  progressBarRef,
  duration,
  setTimeProgress,
  isPlaying,
  togglePlayPause,
  resetPlayer,
}) => {
  const { currentTrack, storyMetas, language, storyFeatures, setActiveTab, activeTab } = useContext(WalkDataContext);
  const [showTranscript, setShowTranscript] = useState(false);

  // Memoize alle Event Handler für bessere Performance
  const skipForward = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime += 15;
    }
  }, [audioRef]);

  const skipBackward = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 15;
    }
  }, [audioRef]);

  const handleImageClick = useCallback(() => {
    if (storyFeatures.includes('track-details') || storyFeatures.includes('track-details-no-icon')) {
      setActiveTab('track-details');
    }
  }, [storyFeatures, setActiveTab]);

  // Memoize teure Berechnungen
  const trackTitle = useMemo(() => 
    getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_name', 'storyMeta_name', ' '),
    [currentTrack, storyMetas, language]
  );

  const trackCover = useMemo(() =>
    getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_cover', 'storyMeta_cover', '/assets/illustrations/placeholder-cover.jpg'),
    [currentTrack, storyMetas, language]
  );

  const transcriptContent = useMemo(() =>
    getTrackMetadataValue(currentTrack, storyMetas, language, 'trackMeta_transcript'),
    [currentTrack, storyMetas, language]
  );

  // Memoize truncateTitle function
  const truncateTitle = useCallback((title, maxLength) => {
    if (title && title.length > maxLength) {
      return title.substring(0, maxLength) + '...';
    }
    return title || '';
  }, []);

  // Memoize truncated title
  const displayTitle = useMemo(() => 
    truncateTitle(trackTitle, 30),
    [trackTitle, truncateTitle]
  );

  // Audio ended event handler
  useEffect(() => {
    const audio = audioRef.current;
    
    const handleEnded = () => {
      setTimeProgress(0);
      resetPlayer();
    };

    if (audio) {
      audio.addEventListener('ended', handleEnded);
      return () => {
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioRef, setTimeProgress, resetPlayer]);

  // Memoize ProgressBar um unnecessary re-renders zu vermeiden
  const memoizedProgressBar = useMemo(() => (
    <ProgressBar
      audioRef={audioRef}
      progressBarRef={progressBarRef}
      duration={duration}
      setTimeProgress={setTimeProgress}
    />
  ), [audioRef, progressBarRef, duration, setTimeProgress]);

  // Render für track-details Tab (große Ansicht)
  if (activeTab === 'track-details') {
    return (
      <>
        <Container className="">
          <div className="controls-wrapper">
            <div className='mt-150 w-90'>
              {memoizedProgressBar}
            </div>
            <div className="controls-pbig mt-1">
              <button onClick={skipBackward} aria-label="Skip backward 15 seconds">
                <FontAwesomeIcon icon={faBackward} />
              </button>
              <button onClick={togglePlayPause} aria-label={isPlaying ? "Pause" : "Play"}>
                {isPlaying ? (
                  <FontAwesomeIcon icon={faPause} size="2x" />
                ) : (
                  <FontAwesomeIcon icon={faPlay} size="2x" />
                )}
              </button>
              <button onClick={skipForward} aria-label="Skip forward 15 seconds">
                <FontAwesomeIcon icon={faForward} />
              </button>
              {transcriptContent && (
                <button
                  onClick={() => setShowTranscript(true)}
                  aria-label="Show transcript"
                  className="btn btn-link cc-button cc-button-big"
                  style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.5rem' }}
                >
                  <FontAwesomeIcon icon={faClosedCaptioning} size="lg" />
                </button>
              )}
            </div>
          </div>
        </Container>
        <TranscriptOverlay
          visible={showTranscript}
          onClose={() => setShowTranscript(false)}
          trackTitle={trackTitle}
          transcriptContent={transcriptContent}
        />
      </>
    );
  }

  // Render für alle anderen Tabs (Mini-Player)
  return (
    <>
      <Container className='ml-150'>
        <div className="audio-player-container-small">
          <div className="player-header">
            <div className="track-thumbnail">
              <img
                className="audio-image-small"
                onClick={handleImageClick}
                src={trackCover}
                alt="Cover"
              />
            </div>
            <div className="controls w-3">
              <button onClick={togglePlayPause} aria-label={isPlaying ? "Pause" : "Play"}>
                {isPlaying ? (
                  <FontAwesomeIcon icon={faPause} size="2x" />
                ) : (
                  <FontAwesomeIcon icon={faPlay} size="2x" />
                )}
              </button>
            </div>
            <div className="track-info">
              <div className="track-title-small">{displayTitle}</div>
              <div className="progress-small">
                {memoizedProgressBar}
              </div>
            </div>
            {transcriptContent && (
              <div className="cc-button-container" style={{ flexShrink: 0, paddingLeft: '0.5rem' }}>
                <button
                  onClick={() => setShowTranscript(true)}
                  aria-label="Show transcript"
                  className="btn btn-link cc-button cc-button-small"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.5rem' }}
                >
                  <FontAwesomeIcon icon={faClosedCaptioning} size="lg" />
                </button>
              </div>
            )}
          </div>
        </div>
      </Container>
      <TranscriptOverlay
        visible={showTranscript}
        onClose={() => setShowTranscript(false)}
        trackTitle={trackTitle}
        transcriptContent={transcriptContent}
      />
    </>
  );
}, (prevProps, nextProps) => {
  // Optimierte Vergleichsfunktion - timeProgress bewusst ausgelassen!
  return prevProps.duration === nextProps.duration &&
         prevProps.isPlaying === nextProps.isPlaying &&
         prevProps.togglePlayPause === nextProps.togglePlayPause &&
         prevProps.resetPlayer === nextProps.resetPlayer &&
         prevProps.setTimeProgress === nextProps.setTimeProgress &&
         prevProps.audioRef === nextProps.audioRef &&
         prevProps.progressBarRef === nextProps.progressBarRef;
         // timeProgress ABSICHTLICH WEGGELASSEN für bessere Performance!
});

export default PlayerDyn;